// SPDX-License-Identifier: AGPL-3.0-or-later
import { splitByWeight, splitEvenly, sum } from "./money.js";
/**
 * Moves one budget line to `requested` and rebalances every other unlocked
 * line the opposite way so the total never changes.
 *
 * - Locked lines never move.
 * - In "equal" mode the difference is shared equally; in "proportional"
 *   mode it is shared by each line's current size.
 * - A line that would drop below $0 stops at $0 and the rest of the
 *   difference is shared among the lines that still have room.
 * - If no other unlocked line exists, nothing changes.
 */
export function rebalance(lines, id, requested, mode = "equal") {
    const next = lines.map((l) => ({ ...l }));
    const target = next.find((l) => l.id === id);
    if (!target)
        throw new Error(`Unknown budget line: ${id}`);
    if (target.locked)
        return { lines: next, applied: target.amount };
    const others = next.filter((l) => l.id !== id && !l.locked);
    if (others.length === 0)
        return { lines: next, applied: target.amount };
    const room = sum(others.map((l) => l.amount));
    const clamped = Math.max(0, Math.min(Math.round(requested), target.amount + room));
    const delta = clamped - target.amount;
    target.amount = clamped;
    if (delta < 0) {
        const shares = mode === "proportional"
            ? splitByWeight(-delta, others.map((l) => l.amount))
            : splitEvenly(-delta, others.length);
        others.forEach((l, i) => {
            l.amount += shares[i] ?? 0;
        });
    }
    else if (delta > 0) {
        let remaining = delta;
        let active = others.filter((l) => l.amount > 0);
        while (remaining > 0 && active.length > 0) {
            const shares = mode === "proportional"
                ? splitByWeight(remaining, active.map((l) => l.amount))
                : splitEvenly(remaining, active.length);
            active.forEach((l, i) => {
                const take = Math.min(shares[i] ?? 0, l.amount);
                l.amount -= take;
                remaining -= take;
            });
            active = active.filter((l) => l.amount > 0);
        }
    }
    return { lines: next, applied: clamped };
}
/** A line's share of the pool as a percentage rounded to two decimals. */
export function toPercent(amount, pool) {
    if (pool === 0)
        return 0;
    return Math.round((amount / pool) * 10000) / 100;
}
/** The amount in cents for a percentage (XX.XX) of the pool. */
export function fromPercent(percent, pool) {
    return Math.round((pool * percent) / 100);
}
//# sourceMappingURL=sliders.js.map